import { detectLanguage, translate, type Language } from '../utils/i18n.js';
import { safeReturnTo } from '../../shared/utils/safeReturnTo.js';
import { carryDesktopParams, desktopProductName, readDesktopClient } from './authLinks.js';
import { desktopDonePath, nextAfterSignIn, requestedReturnTo } from './afterSignIn.js';
import {
  PENDING_LINK_TTL_MS,
  beginOAuthDoor,
  clearPendingLink,
  isRequestId,
  readPendingLink,
  rememberLoginDoor,
  resolveLoginDoor,
  savePendingLink,
} from './loginDoor.js';

/** How often the waiting code box asks whether this browser got signed in (#675 follow-up). */
export const SESSION_WATCH_MS = 5000;

/** POST /api/auth/login-link and /api/auth/register reply (200, 400 or 429) — read defensively. */
type LinkSendBody = { request_id?: unknown; retry_after_s?: unknown; code?: unknown };
/** POST /api/auth/login-link/redeem-code reply — read defensively. */
type LinkRedeemBody = { returnTo?: unknown; reason?: unknown };
/** GET /api/auth/me — only what the name step reads. */
type MeBody = { name_prompt?: unknown; first_name?: unknown; last_name?: unknown };

/**
 * Seconds until a refused link request may be repeated (#667): the server's
 * body figure first, the Retry-After header next, a safe default last (the
 * per-IP limiter answers 429 without the body figure).
 */
function retryAfterSeconds(res: Response, data: unknown): number {
  const fromBody = (data as { retry_after_s?: unknown } | null)?.retry_after_s;
  if (typeof fromBody === 'number' && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(res.headers.get('Retry-After'));
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return 600;
}

/** Same address, whatever the casing or the spaces around it. */
function sameAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Alpine.js data component for authentication page.
 *
 * Manages form state, validation, and API calls via AuthService.
 *
 * `initialTab` comes from the x-data expression (`authPageData('register')`)
 * so the tab is settled at construction — no x-init needed (#667 G18).
 *
 * #675: the signup has no password. `/register` asks for the address and the
 * two names, the server mails the same code + link the login sends, and the
 * code box, the resend, the cooldown and the code check below are ONE state
 * machine for both pages.
 */
export function authPageData(initialTab: 'login' | 'register' = 'login') {
  // #668: settled at CONSTRUCTION, like `desktopClient` below — the first
  // x-show evaluation already sees the right door and a restored code field,
  // so nothing flashes.
  const onLogin = initialTab === 'login';
  const search = new URLSearchParams(window.location.search);
  // #675: the signup page restores ONLY a signup record. The login route
  // hands a handle to an unknown address too; restored here, it would cover
  // the form with a code box for an account that does not exist. And a page
  // opened FOR an address (an invitation's link carries `?email=`) restores
  // no record for another one — that code would sign in the other account
  // and land it on an invitation it cannot accept (codex review).
  const stored = readPendingLink();
  const urlEmail = (search.get('email') ?? '').trim();
  const pending =
    stored && (onLogin || stored.origin === 'register') && (!urlEmail || sameAddress(urlEmail, stored.sentTo))
      ? stored
      : null;
  const pwdOpen = onLogin && resolveLoginDoor(window.location.search) === 'pwd';
  const welcome = onLogin && search.get('welcome') === '1';
  const done = !onLogin && search.get('done') === '1';

  return {
    // Language
    currentLanguage: detectLanguage() as Language,

    /**
     * Validated `?client=` — non-empty when a KingCAD desktop client sent the
     * user here (kingcad#426). Resolved at construction, not in init(), so
     * the desktop hint is there on the first x-show evaluation.
     */
    desktopClient: readDesktopClient(),

    // Translation helper
    t(key: string) {
      return translate(key, this.currentLanguage);
    },

    /**
     * KingCAD mode. Since the browser login (#569) a desktop session is made
     * from ANY web sign-in, Google and Microsoft included, so the mode no
     * longer hides a door (#675) — it only changes where the person ends up.
     */
    isDesktopClient() {
      return this.desktopClient !== '';
    },

    /** "You will use this account to sign in to <product>." */
    desktopHint() {
      return this.t('desktopRegisterHint').replace('{product}', desktopProductName(this.desktopClient));
    },

    /**
     * Copy for a refused login, from the status and the server's machine
     * `code` — never the English `error` string (src/client/CLAUDE.md).
     */
    loginErrorMessage(status: number, data: { code?: string; retryAfter?: number } | null): string {
      if (status === 429) {
        return this.t('authErrTooMany').replace('{seconds}', String(data?.retryAfter ?? 60));
      }
      if (status === 401) return this.t('authErrInvalidCredentials');
      if (status === 403 && data?.code === 'account_disabled') return this.t('authErrAccountDisabled');
      return this.t('loginFailed');
    },

    /** Same for a refused registration (409 and invalid_email are handled before). */
    registerErrorMessage(status: number, data: { code?: string } | null): string {
      if (status === 429) return this.t('resendTooMany');
      if (status === 400 && data?.code === 'weak_password') return this.t('authErrWeakPassword');
      if (status === 409) return this.t('authErrEmailTaken');
      return this.t('registrationFailed');
    },

    // Active tab (login or register)
    activeTab: initialTab,

    // Login form data
    loginForm: {
      email: pending?.sentTo ?? '',
      password: '',
    },

    /**
     * #668: the password door is a fold under the mail door. Open for a
     * browser that last signed in by password, closed for every other one.
     */
    pwdOpen,

    // #569 email login link ("Pošlji mi povezavo za prijavo"). #667: the mail
    // also carries a 6-digit code typed on THIS page, so the session lands in
    // the tab the desktop is watching even when the link opens elsewhere.
    //   idle → sending → code (mail sent, waiting for the code) → verifying
    //   sending → cooldown (429: too many links for the address) → code | idle
    //   sending → error (the first request itself failed)
    linkState: (pending ? 'code' : 'idle') as 'idle' | 'sending' | 'code' | 'cooldown' | 'verifying' | 'error',
    linkSentTo: pending?.sentTo ?? '',
    linkError: '',
    /**
     * Handle of the pending link/code row; only a reply that carries a valid
     * new handle replaces it. Kept in sessionStorage so a refresh restores
     * the code field (#668).
     */
    linkRequestId: pending?.requestId ?? '',
    linkSentAt: pending?.sentAt ?? 0,
    /** The last send was a resend — only the newest code is valid (#668 G21). */
    linkResent: pending?.resent ?? false,
    /** Epoch ms when a refused address may ask again (cooldown countdown). */
    linkRetryAt: 0,
    linkCountdownS: 0,
    linkCountdownTimer: 0,
    linkCode: '',
    linkCodeError: '',
    /**
     * #675 follow-up: while a code is pending, the tab watches for a session —
     * the mailed LINK may be clicked in this same browser, which signs the
     * browser in through the shared cookies without this tab knowing. Timer id
     * of the watch (0 = not watching) and the `visibilitychange` listener.
     */
    sessionWatchTimer: 0,
    sessionWatchListener: null as null | (() => void),

    /**
     * #675 — what »Pošlji znova« needs to REPEAT the request that made the
     * pending code: which route it was, where the person was going, and for a
     * signup the two names. Mirrors the stored record.
     */
    pendingOrigin: pending?.origin ?? (onLogin ? 'login' : 'register'),
    pendingReturnTo: pending?.returnTo ?? '',
    pendingFirstName: pending?.firstName ?? '',
    pendingLastName: pending?.lastName ?? '',

    // Register form data (#675: no password)
    registerForm: {
      email: !onLogin && pending ? pending.sentTo : '',
      first_name: !onLogin && pending ? (pending.firstName ?? '') : '',
      last_name: !onLogin && pending ? (pending.lastName ?? '') : '',
    },

    /** #675: `/register?done=1` — KingCAD mode, signed in, nowhere else to continue. */
    registerDone: done,

    /**
     * #675: the one-time »Kako vam rečemo?« step (`/login?welcome=1`).
     * `pending` until the session is confirmed, so neither the login form nor
     * the step flashes; `off` = the normal login page.
     */
    welcomeState: (welcome ? 'pending' : 'off') as 'off' | 'pending' | 'ask' | 'saving',
    welcomeForm: {
      first_name: '',
      last_name: '',
    },
    welcomeError: '',

    // UI state
    loading: false,
    error: '',

    // #448: login refused because the account awaits email verification —
    // holds the address so the banner can deep-link the pending page.
    unverifiedEmail: '',

    // Email verification state
    verificationStatus: null as 'loading' | 'success' | 'error' | null,
    verificationMessage: '',

    /**
     * Initialize component - check for OAuth callback or email verification token
     */
    async init() {
      const urlParams = new URLSearchParams(window.location.search);

      // #675: the name step owns the page while it is there.
      if (this.welcomeState === 'pending') {
        const owned = await this.startWelcome();
        if (owned) return;
      }

      // A restored code box (refresh) watches for the link click like a fresh one.
      if (this.linkState === 'code') {
        this.startSessionWatch();
      }

      // Check if we're returning from OAuth (auth=success query param)
      if (urlParams.get('auth') === 'success') {
        window.location.href = safeReturnTo(urlParams.get('returnTo'), window.location.origin);
        return;
      }

      // Surface an OAuth failure the server redirected us here with. Without
      // this the refusal is silent — the user just lands back on /login with no
      // idea why sign-in did not work (issue #447).
      if (urlParams.get('error') === 'oauth_email_conflict') {
        this.error = this.t('oauthEmailConflict');
      }

      // #448: a new Microsoft account was refused because the profile carried
      // no mailbox (UPN-only) — point at the signup.
      if (urlParams.get('error') === 'oauth_no_email') {
        this.error = this.t('oauthNoEmail');
      }

      // #448: prefill the address. Producers: the invitation CTA
      // (/register?email=<invitee>), the verify-email success CTA
      // (/login?email=<verified address>), and authLink forwarding. A
      // restored code box keeps the address its code went to.
      const email = urlParams.get('email');
      if (email && !this.linkRequestId) {
        this.loginForm.email = email;
        this.registerForm.email = email;
      }

      // Check for email verification token
      const token = urlParams.get('token');
      if (token) {
        await this.verifyEmail(token);
      }
    },

    /**
     * Link to the /verify-email pending page for an address (#448), carrying
     * the current returnTo so the resent link keeps the continuation.
     */
    verifyEmailLink(email: string) {
      const urlParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();
      if (email) {
        params.set('pending', email);
      }
      const returnTo = safeReturnTo(urlParams.get('returnTo'), window.location.origin, '');
      if (returnTo) {
        params.set('returnTo', returnTo);
      }
      carryDesktopParams(params);
      const qs = params.toString();
      return qs ? `/verify-email?${qs}` : '/verify-email';
    },

    /**
     * "Forgot password?" link. Prefers the address the user has actually typed
     * over the one that came in on the URL, so a correction made on this page
     * is not thrown away by the hop.
     */
    forgotPasswordLink(email?: string) {
      const urlParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();

      const address = email || urlParams.get('email') || '';
      if (address) {
        params.set('email', address);
      }
      carryDesktopParams(params);

      const qs = params.toString();
      return qs ? `/forgot-password?${qs}` : '/forgot-password';
    },

    /**
     * Verify email with token from URL
     */
    async verifyEmail(token: string) {
      this.verificationStatus = 'loading';
      this.verificationMessage = this.t('verifyingEmail');

      try {
        let response: Response;
        try {
          response = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } catch {
          this.verificationStatus = 'error';
          this.verificationMessage = this.t('networkError');
          return;
        }

        let data: any;
        try {
          data = await response.json();
        } catch {
          this.verificationStatus = 'error';
          this.verificationMessage = this.t('serverUnavailable');
          return;
        }

        if (response.ok) {
          this.verificationStatus = 'success';
          this.verificationMessage = this.t('emailVerifiedSuccess');
          this.activeTab = 'login';

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          this.verificationStatus = 'error';
          this.verificationMessage = data.error || this.t('verificationFailedMessage');
        }
      } catch (err) {
        this.verificationStatus = 'error';
        this.verificationMessage = this.t('emailVerificationFailed');
      }
    },

    /**
     * #675: leave the page after a sign-in the page SAW succeed. `returnTo` is
     * safe or empty; `nextAfterSignIn` may put the name step in front of it.
     */
    async leaveSignedIn(returnTo: string) {
      this.stopSessionWatch();
      window.location.href = await nextAfterSignIn(returnTo);
    },

    /**
     * Where the waiting code box continues: the page's own returnTo, else the
     * one the code was asked with (a restored device approval on a
     * `?client=` page must win over the done page), else the done page.
     */
    pendingDestination() {
      const own = safeReturnTo(new URLSearchParams(window.location.search).get('returnTo'), window.location.origin, '');
      return own || this.pendingReturnTo || desktopDonePath();
    },

    /**
     * #675 follow-up: is THIS browser signed in as the address the code went
     * to? True = the mailed link was clicked here — continue as signed in, no
     * code needed. The address must match: an older session of somebody else
     * (an invitee's colleague, a shared machine) must never carry the waiting
     * page away. A failed or slow answer is »no«.
     */
    async checkSignedIn(): Promise<boolean> {
      const handle = this.linkRequestId;
      const address = this.linkSentTo;
      if (!handle || !address) return false;
      // Bounded (codex review): a stalled answer must not hold »Potrdi kodo«
      // in its verifying state with no way out.
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), SESSION_WATCH_MS);
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include', signal: controller.signal });
        if (!res.ok) return false;
        const me = (await res.json()) as { email?: unknown } | null;
        if (typeof me?.email !== 'string' || !sameAddress(me.email, address)) return false;
      } catch {
        return false;
      } finally {
        window.clearTimeout(timer);
      }
      // The box was closed or replaced (another address asked) meanwhile:
      // this answer belongs to an older attempt.
      if (this.linkRequestId !== handle) return false;
      this.stopLinkCountdown();
      rememberLoginDoor('mail');
      clearPendingLink();
      await this.leaveSignedIn(this.pendingDestination());
      return true;
    },

    /**
     * The watch: one check on every return to the tab (the usual path — the
     * link is clicked in the mail client, a new tab opens, the person comes
     * back), one every SESSION_WATCH_MS while the tab is visible — for as long
     * as the code lives (PENDING_LINK_TTL_MS from the send). No check at the
     * start: nobody can have clicked the link yet. Never while hidden: a tab
     * left open all day costs nothing.
     */
    startSessionWatch() {
      this.stopSessionWatch();
      if (!this.linkRequestId) return;
      const tick = () => {
        if (!this.linkRequestId || Date.now() - this.linkSentAt >= PENDING_LINK_TTL_MS) {
          this.stopSessionWatch();
          return;
        }
        if (document.hidden) return;
        void this.checkSignedIn();
      };
      this.sessionWatchListener = () => {
        if (!document.hidden) tick();
      };
      document.addEventListener('visibilitychange', this.sessionWatchListener);
      this.sessionWatchTimer = window.setInterval(tick, SESSION_WATCH_MS);
    },

    stopSessionWatch() {
      if (this.sessionWatchTimer) {
        window.clearInterval(this.sessionWatchTimer);
        this.sessionWatchTimer = 0;
      }
      if (this.sessionWatchListener) {
        document.removeEventListener('visibilitychange', this.sessionWatchListener);
        this.sessionWatchListener = null;
      }
    },

    /**
     * Handle login form submission.
     */
    async handleLogin() {
      this.loading = true;
      this.error = '';
      this.unverifiedEmail = '';

      try {
        let response: Response;
        try {
          response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: this.loginForm.email,
              password: this.loginForm.password,
            }),
          });
        } catch {
          throw new Error(this.t('networkError'));
        }

        let data: any;
        try {
          data = await response.json();
        } catch {
          throw new Error(this.t('serverUnavailable'));
        }

        if (!response.ok) {
          // #448: the account exists but awaits email verification — show the
          // dedicated banner with a link to the pending/resend page instead of
          // a flat "Account disabled".
          if (response.status === 403 && data.code === 'email_not_verified') {
            this.unverifiedEmail = data.email || this.loginForm.email;
            return;
          }
          throw new Error(this.loginErrorMessage(response.status, data));
        }

        // Success! The page's own returnTo, else the done page in KingCAD mode.
        rememberLoginDoor('pwd');
        await this.leaveSignedIn(requestedReturnTo());
      } catch (err: any) {
        this.error = err.message || this.t('loginFailed');
      } finally {
        this.loading = false;
      }
    },

    /**
     * #668: the one submit of the login form (Enter in a field, »Prijavi se«).
     * The fold decides the door: open → password, closed → mailed code.
     */
    submitMain() {
      return this.pwdOpen ? this.handleLogin() : this.sendLoginLink();
    },

    /** The fold's `toggle` event — the person, or the browser's find-in-page. */
    setPwdOpen(open: boolean) {
      this.pwdOpen = open;
    },

    /**
     * #569: request an emailed login link for the typed address. Always
     * answers "sent" (the server never confirms whether an account exists);
     * the stored returnTo carries the continuation — the device approval page
     * in the desktop flow — through the mail click. #667: the reply carries
     * the row's handle so the code from the mail can be typed here; a 429
     * (4 links per 10 min, uniform for every address) starts the countdown.
     * Also the »Pošlji znova« handler — a fresh handle replaces the old one,
     * and the server marks the older code used (#668: only the newest counts).
     *
     * #675: »Pošlji znova« REPEATS the request that made the pending code. A
     * signup record repeats the signup, with the names kept in the record —
     * that call creates the account when the first one has not finished or
     * has failed, so a resend can always recover. It does so only for the
     * address the record was made for: a different address typed on the login
     * page is a new login request.
     */
    async sendLoginLink() {
      if (this.linkState === 'sending' || this.linkState === 'verifying' || this.linkState === 'cooldown') return;
      const resend = this.linkRequestId !== '';
      // The signup page shows no address field next to the code box: its
      // resend is for the address the code went to.
      const email = (onLogin ? this.loginForm.email : this.linkSentTo).trim();
      this.linkError = '';
      this.linkCodeError = '';
      if (!email) {
        this.linkError = this.t('loginLinkNeedEmail');
        return;
      }
      const repeatsSignup =
        resend &&
        this.pendingOrigin === 'register' &&
        sameAddress(email, this.linkSentTo) &&
        this.pendingFirstName !== '' &&
        this.pendingLastName !== '';
      await this.requestCode(
        repeatsSignup
          ? { origin: 'register', email, firstName: this.pendingFirstName, lastName: this.pendingLastName }
          : { origin: 'login', email },
        resend
      );
    },

    /**
     * The one request behind both pages' code box: the login's code request
     * or the signup, by `origin`. Both answer `200 { request_id }` for every
     * address and 429 with the same figures.
     *
     * A reply that never arrives, or a 200 that cannot be read, is AMBIGUOUS:
     * the server may have issued the new row and spent the one this page
     * holds. The handle is therefore replaced only by a 200 that carries a
     * valid one; otherwise the page keeps what it has and says how to go on.
     */
    async requestCode(
      ask:
        | { origin: 'login'; email: string }
        | { origin: 'register'; email: string; firstName: string; lastName: string },
      resend: boolean
    ) {
      this.linkState = 'sending';
      // The watch belongs to the code being replaced — a sign-in as the OLD
      // address must not carry away a request for a new one (codex review).
      this.stopSessionWatch();
      try {
        // The page's own destination first; a resend from a page that has
        // none sends the one the pending code was asked with.
        const returnTo = requestedReturnTo() || (resend ? this.pendingReturnTo : '');
        let res: Response;
        try {
          res = await fetch(ask.origin === 'register' ? '/api/auth/register' : '/api/auth/login-link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              email: ask.email,
              ...(ask.origin === 'register' ? { first_name: ask.firstName, last_name: ask.lastName } : {}),
              returnTo: returnTo || undefined,
              // The mail is composed server-side, in a request that cannot
              // see this page's language (kingcad#426).
              lang: this.currentLanguage,
            }),
          });
        } catch {
          this.failLinkSend(resend, this.t('loginLinkResendUnsure'));
          return;
        }
        let data: LinkSendBody | null = null;
        try {
          data = (await res.json()) as LinkSendBody;
        } catch {
          data = null;
        }
        if (res.status === 429) {
          this.startLinkCooldown(retryAfterSeconds(res, data));
          return;
        }
        if (!res.ok) {
          this.failLinkSend(resend, this.sendErrorText(res.status, data?.code));
          return;
        }
        const requestId = data?.request_id;
        if (!isRequestId(requestId)) {
          this.failLinkSend(resend, this.t('loginLinkResendUnsure'));
          return;
        }
        this.linkSentTo = ask.email;
        this.linkRequestId = requestId;
        this.linkSentAt = Date.now();
        this.linkResent = resend;
        this.linkCode = '';
        this.linkState = 'code';
        this.pendingOrigin = ask.origin;
        this.pendingReturnTo = returnTo;
        this.pendingFirstName = ask.origin === 'register' ? ask.firstName : '';
        this.pendingLastName = ask.origin === 'register' ? ask.lastName : '';
        savePendingLink({
          requestId,
          sentAt: this.linkSentAt,
          sentTo: ask.email,
          resent: resend,
          origin: ask.origin,
          returnTo,
          ...(ask.origin === 'register' ? { firstName: ask.firstName, lastName: ask.lastName } : {}),
        });
        this.startSessionWatch();
      } catch {
        this.failLinkSend(resend, this.t('loginLinkResendUnsure'));
      }
    },

    /**
     * A refused request → sentence. Branches on the status and the machine
     * `code`, never on the server's `error` text (src/client/CLAUDE.md).
     */
    sendErrorText(status: number, code: unknown): string {
      if (status === 400 && code === 'invalid_email') return this.t('resendInvalidEmail');
      if (status === 400 && code === 'name_required') return this.t('registerNameRequired');
      return this.t('loginLinkError');
    },

    /**
     * A failed FIRST send is today's error state (the door reports it); a
     * failed resend keeps the code form and the handle, and reports next to
     * »Pošlji znova«. Whether the earlier code is still valid depends on the
     * failure: an HTTP error issued nothing, an ambiguous one may have.
     */
    failLinkSend(resend: boolean, message: string) {
      if (resend) {
        this.linkCodeError = message;
        this.linkState = this.linkRestingState();
        // The earlier code may still be live — keep watching for its link.
        this.startSessionWatch();
        return;
      }
      this.linkState = 'error';
      this.linkError = message;
    },

    /** The state to return to once nothing is in flight. */
    linkRestingState(): 'cooldown' | 'code' | 'idle' {
      if (this.linkRetryAt > Date.now()) return 'cooldown';
      return this.linkRequestId ? 'code' : 'idle';
    },

    startLinkCooldown(seconds: number) {
      this.stopLinkCountdown();
      this.linkRetryAt = Date.now() + seconds * 1000;
      this.linkState = 'cooldown';
      this.tickLinkCountdown();
      if (this.linkState === 'cooldown') {
        this.linkCountdownTimer = window.setInterval(() => this.tickLinkCountdown(), 1000);
      }
    },

    tickLinkCountdown() {
      const left = Math.ceil((this.linkRetryAt - Date.now()) / 1000);
      this.linkCountdownS = Math.max(0, left);
      if (left > 0) return;
      this.stopLinkCountdown();
      // A code check in flight settles its own state when it returns.
      if (this.linkState === 'cooldown') {
        this.linkState = this.linkRequestId ? 'code' : 'idle';
      }
    },

    stopLinkCountdown() {
      if (this.linkCountdownTimer) {
        window.clearInterval(this.linkCountdownTimer);
        this.linkCountdownTimer = 0;
      }
    },

    /** Alpine calls this when the component's element is removed. */
    destroy() {
      this.stopLinkCountdown();
      this.stopSessionWatch();
    },

    /**
     * The code form stays visible through a resend (state passes through
     * 'sending') — hiding it would drop the typed code and jump the layout.
     */
    linkCodeFormVisible() {
      if (this.linkState === 'code' || this.linkState === 'cooldown' || this.linkState === 'verifying') return true;
      return this.linkState === 'sending' && this.linkRequestId !== '';
    },

    /** »Pošlji znova« — an honest countdown while the address is refused. */
    linkResendLabel() {
      if (this.linkState === 'sending') return this.t('loginLinkSending');
      if (this.linkState === 'cooldown') {
        const s = this.linkCountdownS;
        return s >= 60
          ? this.t('loginLinkResendIn').replace('{min}', String(Math.ceil(s / 60)))
          : this.t('loginLinkResendInS').replace('{s}', String(s));
      }
      return this.t('loginLinkResend');
    },

    /**
     * Screen-reader announcement, mirrored from the code form: that form
     * toggles display, and a live region that appears together with its text
     * is not announced, so the standing live region in the password form
     * carries the same sentence.
     */
    linkStatusText() {
      if (this.linkState === 'cooldown') return this.linkResendLabel();
      if (this.linkRequestId && (this.linkState === 'code' || this.linkState === 'verifying')) {
        return `${this.linkSentSentence()} ${this.t('loginLinkSentCode')}`;
      }
      return '';
    },

    /**
     * What the code box says first. After a resend: only the newest code is
     * valid (#668 G21). A signup says plainly that the code was sent (#675 —
     * every address gets a mail there); a login keeps the neutral "if the
     * account exists" line.
     */
    linkSentSentence() {
      if (this.linkResent) return this.t('loginLinkResent');
      const key = this.pendingOrigin === 'register' ? 'registerCodeSent' : 'loginLinkSent';
      return this.t(key).replace('{email}', this.linkSentTo);
    },

    /**
     * #667: type the 6-digit code from the mail. On success the server has
     * set the session cookies. Where next: the page's own returnTo, else the
     * one the server stored with the code (a restored code typed on a bare
     * /login must still reach its invitation or approval page), else the done
     * page in KingCAD mode.
     */
    async submitLinkCode() {
      if (this.linkState === 'verifying' || this.linkState === 'sending') return;
      const code = this.linkCode.replace(/\s+/g, '');
      this.linkCodeError = '';
      if (!/^\d{6}$/.test(code)) {
        this.linkCodeError = this.t('loginLinkCodeWrong');
        return;
      }
      if (!this.linkRequestId) {
        this.linkCodeError = this.t('loginLinkCodeExpired');
        return;
      }
      this.linkState = 'verifying';
      try {
        const res = await fetch('/api/auth/login-link/redeem-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ request_id: this.linkRequestId, code }),
        });
        let data: LinkRedeemBody | null = null;
        try {
          data = (await res.json()) as LinkRedeemBody;
        } catch {
          data = null;
        }
        if (res.ok) {
          this.stopLinkCountdown();
          rememberLoginDoor('mail');
          clearPendingLink();
          const urlParams = new URLSearchParams(window.location.search);
          const own = safeReturnTo(urlParams.get('returnTo'), window.location.origin, '');
          const stored = safeReturnTo(
            typeof data?.returnTo === 'string' ? data.returnTo : null,
            window.location.origin,
            ''
          );
          await this.leaveSignedIn(own || stored || desktopDonePath());
          return;
        }
        // »used« after the LINK was clicked in this browser: the browser is
        // signed in already — continue instead of refusing (#675 follow-up).
        if (res.status === 400 && data?.reason === 'used' && (await this.checkSignedIn())) {
          return;
        }
        this.linkCodeError = this.linkCodeErrorText(res.status, data?.reason);
        // A dead handle must not come back on a refresh. Only the STORED
        // record goes — the form, this sentence and »Pošlji znova« stay.
        if (res.status === 403 || (res.status === 400 && (data?.reason === 'used' || data?.reason === 'expired'))) {
          clearPendingLink();
        }
      } catch {
        this.linkCodeError = this.t('networkError');
      }
      this.linkState = this.linkRestingState();
    },

    /** Server refusal → sentence. `wrong_code` and `invalid` read the same. */
    linkCodeErrorText(status: number, reason: unknown): string {
      if (status === 403) return this.t('loginLinkCodeRefused');
      if (status === 429) return this.t('loginLinkCodeTooMany');
      if (status === 400) {
        if (reason === 'expired') return this.t('loginLinkCodeExpired');
        if (reason === 'used') return this.t('loginLinkCodeUsed');
        return this.t('loginLinkCodeWrong');
      }
      return this.t('serverUnavailable');
    },

    /**
     * Handle register form submission (#675).
     *
     * No password. The server answers the same for every address and mails a
     * code; the page shows the code box with the address the code went to —
     * that visibility is what catches a typed-in-wrong address (the
     * tina.skledar@gvo.com case), and »Popravite ga« leads back to the form.
     *
     * The browser has already checked `required` and the address format; a
     * name of spaces only is caught here.
     */
    async handleRegister() {
      if (this.linkState === 'sending' || this.linkState === 'verifying') return;
      this.error = '';
      this.linkError = '';
      this.linkCodeError = '';

      const email = this.registerForm.email.trim();
      const firstName = this.registerForm.first_name.trim();
      const lastName = this.registerForm.last_name.trim();
      if (!email) {
        this.linkError = this.t('loginLinkNeedEmail');
        return;
      }
      if (!firstName || !lastName) {
        this.linkError = this.t('registerNameRequired');
        return;
      }

      this.loading = true;
      try {
        // A first request from the form, never a resend: the form is only on
        // screen when there is no pending code.
        await this.requestCode({ origin: 'register', email, firstName, lastName }, false);
      } finally {
        this.loading = false;
      }
    },

    /** The signup form is on screen only while no code is pending and the page is not the done page. */
    registerFormVisible() {
      return !this.registerDone && !this.linkCodeFormVisible();
    },

    /**
     * »Napačen e-naslov? Popravite ga« — back to the form with what was
     * typed. The pending record goes: its code was for the wrong address.
     */
    editRegisterAddress() {
      if (this.linkState === 'sending' || this.linkState === 'verifying') return;
      this.stopLinkCountdown();
      this.stopSessionWatch();
      clearPendingLink();
      this.registerForm.email = this.linkSentTo;
      this.registerForm.first_name = this.pendingFirstName || this.registerForm.first_name;
      this.registerForm.last_name = this.pendingLastName || this.registerForm.last_name;
      this.linkRequestId = '';
      this.linkSentTo = '';
      this.linkResent = false;
      this.linkCode = '';
      this.linkError = '';
      this.linkCodeError = '';
      this.linkRetryAt = 0;
      this.linkState = 'idle';
    },

    /** The done page: "Return to <product> and press »Prijava v brskalniku«." */
    registerDoneText() {
      return this.t('registerDoneBody').replace('{product}', desktopProductName(this.desktopClient));
    },

    /** Where the name step goes on to. */
    welcomeReturnTo() {
      const urlParams = new URLSearchParams(window.location.search);
      return safeReturnTo(urlParams.get('returnTo'), window.location.origin);
    },

    /**
     * #675: `/login?welcome=1`. Shown only to a signed-in person whose name is
     * missing and who was never asked. No session → the normal login page.
     * Nothing to ask (any more) → straight on.
     *
     * @returns true when the step owns the page (shown, or leaving) — init()
     *   then does nothing else; false when the normal login page renders.
     */
    async startWelcome(): Promise<boolean> {
      let me: MeBody | null = null;
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.status === 401 || res.status === 403) {
          this.welcomeState = 'off';
          return false;
        }
        me = res.ok ? ((await res.json()) as MeBody) : null;
      } catch {
        me = null;
      }
      if (me?.name_prompt !== true) {
        // Signed in with nothing to ask, or the question could not be read:
        // the step never stands in the way.
        window.location.href = this.welcomeReturnTo();
        return true;
      }
      this.welcomeForm.first_name = typeof me.first_name === 'string' ? me.first_name : '';
      this.welcomeForm.last_name = typeof me.last_name === 'string' ? me.last_name : '';
      this.welcomeState = 'ask';
      return true;
    },

    /** »Shrani in nadaljuj« */
    async saveWelcome() {
      if (this.welcomeState !== 'ask') return;
      const firstName = this.welcomeForm.first_name.trim();
      const lastName = this.welcomeForm.last_name.trim();
      this.welcomeError = '';
      if (!firstName || !lastName) {
        this.welcomeError = this.t('registerNameRequired');
        return;
      }
      this.welcomeState = 'saving';
      try {
        const res = await fetch('/api/auth/profile', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ first_name: firstName, last_name: lastName }),
        });
        if (res.ok) {
          window.location.href = this.welcomeReturnTo();
          return;
        }
        this.welcomeError = this.t('welcomeSaveFailed');
      } catch {
        this.welcomeError = this.t('networkError');
      }
      this.welcomeState = 'ask';
    },

    /**
     * »Preskoči« — the person is never asked again. The step never blocks:
     * the page goes on whether or not the mark could be stored.
     */
    async skipWelcome() {
      if (this.welcomeState !== 'ask') return;
      this.welcomeState = 'saving';
      try {
        await fetch('/api/auth/name-prompt/dismiss', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          // A bodyless POST with this header is refused before the handler.
          body: JSON.stringify({}),
        });
      } catch {
        /* going on is the point */
      }
      window.location.href = this.welcomeReturnTo();
    },

    /**
     * Build a link to the sibling auth page, carrying the current returnTo
     * (and the address) with it. Without this, an invitee who lands on
     * /register?returnTo=/invite/<id> and clicks "Sign in" loses the invitation
     * target and never reaches the accept step.
     *
     * #675: the address the person has TYPED goes first — somebody who asked
     * for a code on the login page and then pressed »Ustvarite ga« must not
     * type it twice. The address from the URL is the fallback.
     *
     * returnTo is forwarded only when safeReturnTo resolves it to a same-origin
     * path. The predicate this replaced (startsWith('/') && !startsWith('//'))
     * looked sufficient but was not: browsers fold "\" into "/" and strip raw
     * TAB/LF/CR before parsing, so "/\evil.example" passed it and still
     * navigated off-site (issue #452).
     */
    authLink(path: string) {
      const urlParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();

      const returnTo = safeReturnTo(urlParams.get('returnTo'), window.location.origin, '');
      if (returnTo) {
        params.set('returnTo', returnTo);
      }

      const typed = (onLogin ? this.loginForm.email : this.registerForm.email || this.linkSentTo).trim();
      const email = typed || urlParams.get('email');
      if (email) {
        params.set('email', email);
      }

      // Keep the desktop session's language + client id across the hop
      // (kingcad#426) — otherwise /register?client=sewer&lang=hr sends the user
      // to a Slovenian /login.
      carryDesktopParams(params);

      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },

    /**
     * Start an OAuth flow.
     *
     * `lang` rides along so the server can stash it for the callback: the
     * provider redirect lands on a fresh request that has none of this page's
     * context, and any mail sent from there would otherwise default.
     *
     * #675: the destination is the page's own returnTo, else the done page in
     * KingCAD mode — the callback redirects there server-side.
     */
    oauthUrl(provider: 'google' | 'microsoft') {
      const params = new URLSearchParams({ lang: this.currentLanguage });

      const returnTo = requestedReturnTo();
      if (returnTo) {
        params.set('returnTo', returnTo);
      }

      return `/api/auth/oauth/${provider}?${params.toString()}`;
    },

    /**
     * Redirect to Google OAuth.
     */
    loginWithGoogle() {
      beginOAuthDoor('google');
      window.location.href = this.oauthUrl('google');
    },

    /**
     * Redirect to Microsoft OAuth.
     */
    loginWithMicrosoft() {
      beginOAuthDoor('microsoft');
      window.location.href = this.oauthUrl('microsoft');
    },
  };
}
